import MenuBookIcon from "@mui/icons-material/MenuBook";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import type { KnowledgeBaseLibraryEntry } from "knowledge-base/_api/knowledge-base-api";
import { clientLibraries } from "knowledge-base/libraries/client-libraries";
import { clientLibraryPath } from "knowledge-base/knowledge-base-locations";
import { Link } from "react-router";

/**
 * The knowledge bases of clients this firm works for, named and linked.
 *
 * The switcher above the contents can already reach these, but only as a closed dropdown on a page
 * a reader has to have arrived at first — which meant a firm could work for a client for months
 * without discovering that the client had published rules at all. A shelf says how many there are
 * and whose they are without anyone having to open anything.
 *
 * Absent entirely for a firm with no clients, rather than an empty heading: most organizations have
 * none, and a permanently empty section reads as something broken.
 */
export default function ClientLibraryShelf({
  libraries,
  reading,
}: Readonly<{
  libraries: readonly KnowledgeBaseLibraryEntry[];
  reading?: KnowledgeBaseLibraryEntry;
}>) {
  const clients = clientLibraries(libraries);

  if (clients.length === 0) return null;

  return (
    <>
      <Divider sx={{ my: 3 }} />

      <Typography component="h2" gutterBottom variant="h6">
        Knowledge bases you can read
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Organizations you do work for. You can read their knowledge bases; only they can change
        them.
      </Typography>

      <List disablePadding>
        {clients.map((client) => (
          <ListItemButton
            component={Link}
            key={client.ownerLegalEntityId}
            selected={reading?.ownerLegalEntityId === client.ownerLegalEntityId}
            to={clientLibraryPath(client.ownerLegalEntityId)}
          >
            <ListItemIcon>
              <MenuBookIcon color="primary" />
            </ListItemIcon>
            {/*
              The name alone. The heading above already says what these are, so a second line
              reading "<name>'s knowledge base" under a row titled "<name>" said it twice.
            */}
            <ListItemText primary={client.ownerName} />
          </ListItemButton>
        ))}
      </List>
    </>
  );
}
